import { useCallback, useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { SwimlaneColor } from '@bliks-io/contracts';
import type { UndoableEditorActions } from './useUndoableEditorActions';

export interface SwimlaneActions {
  onMoveUp: (laneKey: string, currentOrder: string[]) => void;
  onMoveDown: (laneKey: string, currentOrder: string[]) => void;
  onContextMenuMove: (laneKey: string, direction: 'up' | 'down') => void;
  onContextMenuSetColor: (laneKey: string, color: string | null) => void;
  getColor: (laneKey: string) => string | null;
  getCapabilities: (laneKey: string) => { moveUp: boolean; moveDown: boolean };
  findSwimlaneAt: (position: { x: number; y: number }) => string | null;
  /**
   * Convert an absolute flow position into one stored relative to the lane
   * with `laneKey`. Lane-children render at `lane.position + child.position`,
   * and the lane layout adds `upGrowth` back at render time, so the stored
   * baseY must subtract both. Returns the input unchanged when the lane
   * isn't found (fallback that keeps callers from blowing up on a stale id).
   */
  toLaneRelativePosition: (
    absolute: { x: number; y: number },
    laneKey: string
  ) => { x: number; y: number };
}

/**
 * Two entry points: hover buttons close over `currentOrder` from the builder
 * and pass it back; the context menu has no such closure, so it derives the
 * order from React Flow's swimlane nodes sorted by `position.y`.
 *
 * `laneColors` is the PERSISTED layout mapping, not a node-graph scrape:
 * color writes must preserve entries for lanes that aren't rendered right now
 * (the renderer merely ignores them), so the persisted array is the only
 * correct base for the wholesale-replace patch.
 */
export function useSwimlaneActions(
  undoableActions: UndoableEditorActions,
  laneColors: readonly SwimlaneColor[] = []
): SwimlaneActions {
  const { getNodes } = useReactFlow();

  const onMoveUp = useCallback(
    (laneKey: string, currentOrder: string[]) => {
      void undoableActions.moveSwimlane(laneKey, 'up', currentOrder);
    },
    [undoableActions]
  );

  const onMoveDown = useCallback(
    (laneKey: string, currentOrder: string[]) => {
      void undoableActions.moveSwimlane(laneKey, 'down', currentOrder);
    },
    [undoableActions]
  );

  const resolveOrder = useCallback((): string[] => {
    return getNodes()
      .filter((n) => n.type === 'flowSwimlane')
      .sort((a, b) => a.position.y - b.position.y)
      .map((n) => (n.data as { laneKey?: string } | undefined)?.laneKey)
      .filter((key): key is string => typeof key === 'string');
  }, [getNodes]);

  const onContextMenuSetColor = useCallback(
    (laneKey: string, color: string | null) => {
      void undoableActions.setSwimlaneColor(laneKey, color, laneColors);
    },
    [undoableActions, laneColors]
  );

  const getColor = useCallback(
    (laneKey: string): string | null =>
      laneColors.find((entry) => entry.laneKey === laneKey)?.color ?? null,
    [laneColors]
  );

  const onContextMenuMove = useCallback(
    (laneKey: string, direction: 'up' | 'down') => {
      void undoableActions.moveSwimlane(laneKey, direction, resolveOrder());
    },
    [undoableActions, resolveOrder]
  );

  const getCapabilities = useCallback(
    (laneKey: string) => {
      const order = resolveOrder();
      const idx = order.indexOf(laneKey);
      return {
        moveUp: idx > 0,
        moveDown: idx >= 0 && idx < order.length - 1
      };
    },
    [resolveOrder]
  );

  const findSwimlaneAt = useCallback(
    (position: { x: number; y: number }): string | null => {
      for (const node of getNodes()) {
        if (node.type !== 'flowSwimlane') continue;
        const data = node.data as { width?: number; height?: number; laneKey?: string } | undefined;
        if (!data?.laneKey || !data.width || !data.height) continue;
        const x0 = node.position.x;
        const y0 = node.position.y;
        if (
          position.x >= x0 &&
          position.x <= x0 + data.width &&
          position.y >= y0 &&
          position.y <= y0 + data.height
        ) {
          return data.laneKey;
        }
      }
      return null;
    },
    [getNodes]
  );

  const toLaneRelativePosition = useCallback(
    (absolute: { x: number; y: number }, laneKey: string): { x: number; y: number } => {
      const lane = getNodes().find(
        (n) =>
          n.type === 'flowSwimlane' &&
          (n.data as { laneKey?: string } | undefined)?.laneKey === laneKey
      );
      if (!lane) {
        // Lane doesn't exist yet (e.g. clicking in empty space when there's
        // no unassigned step yet — the lane will materialize *with* this
        // step). The absolute click y is meaningless without a lane offset
        // to subtract; landing at lane-local 0 keeps the new lane at its
        // natural minimum height instead of stretching to absolute-y depth.
        return { x: 0, y: 0 };
      }
      const upGrowth = (lane.data as { upGrowth?: number } | undefined)?.upGrowth ?? 0;
      // baseY = absoluteY - lane.position.y - upGrowth so that the renderer's
      // `child.position.y = baseY + upGrowth` lands at the requested absolute
      // y once the lane container offsets it. On the x-axis absolute equals
      // base (lane x = -leftGrowth, child x = baseX + leftGrowth), so the
      // absolute click x is already the stored base x.
      return {
        x: absolute.x,
        y: absolute.y - lane.position.y - upGrowth
      };
    },
    [getNodes]
  );

  return useMemo(
    () => ({
      onMoveUp,
      onMoveDown,
      onContextMenuMove,
      onContextMenuSetColor,
      getColor,
      getCapabilities,
      findSwimlaneAt,
      toLaneRelativePosition
    }),
    [
      onMoveUp,
      onMoveDown,
      onContextMenuMove,
      onContextMenuSetColor,
      getColor,
      getCapabilities,
      findSwimlaneAt,
      toLaneRelativePosition
    ]
  );
}
